import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import type { CategoryHue, MealPlanEntry, MealSlot } from '@recipes/contracts';
import { mealSlotHue } from '@recipes/domain';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  cn,
} from '@recipes/ui';
import { format, isToday, parseISO } from 'date-fns';
import { de as deLocale, enUS } from 'date-fns/locale';
import { GripVertical, MoreHorizontal, MoveRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { HueDot } from '@/features/appearance/hue';
import { mealSlotLabel } from '@/features/shared/labels';

export const STANDARD_SLOTS: MealSlot[] = ['BREAKFAST', 'LUNCH', 'DINNER', 'SNACK'];

export interface SlotTarget {
  date: string;
  slot: MealSlot;
  customSlotName: string | null;
}

export function dateLocale(language: string) {
  return language.startsWith('de') ? deLocale : enUS;
}

export function entryTitle(entry: MealPlanEntry): string {
  return entry.recipeTitle ?? entry.title ?? '';
}

function slotKey(date: string, slot: MealSlot, custom: string | null) {
  return `${date}|${slot}|${custom ?? ''}`;
}
function parseSlotKey(key: string): SlotTarget {
  const [date = '', slot = 'DINNER', custom = ''] = key.split('|');
  return { date, slot: slot as MealSlot, customSlotName: custom || null };
}

/**
 * Week view: on phones a list of days with their slots, on md+ a 7-column grid. Entries
 * are draggable between slots (pointer + keyboard); the "move" menu is the explicit
 * keyboard/screen-reader alternative.
 */
export function WeekGrid({
  days,
  entries,
  onAdd,
  onEdit,
  onMove,
  onRemove,
  onMoveRequest,
}: {
  days: string[];
  entries: MealPlanEntry[];
  onAdd: (target: SlotTarget) => void;
  onEdit: (entry: MealPlanEntry) => void;
  onMove: (entry: MealPlanEntry, target: SlotTarget) => void;
  onRemove: (entry: MealPlanEntry) => void;
  onMoveRequest: (entry: MealPlanEntry) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = dateLocale(i18n.language);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );
  const customNames = [
    ...new Set(
      entries
        .filter((e) => e.slot === 'CUSTOM')
        .map((e) => e.customSlotName ?? t('mealPlan.customSlot')),
    ),
  ];
  const slotRows: Array<{ slot: MealSlot; custom: string | null; label: string }> = [
    ...STANDARD_SLOTS.map((s) => ({ slot: s, custom: null, label: mealSlotLabel(t, s) })),
    ...customNames.map((name) => ({ slot: 'CUSTOM' as MealSlot, custom: name, label: name })),
  ];
  const entriesFor = (date: string, slot: MealSlot, custom: string | null) =>
    entries
      .filter(
        (e) =>
          e.date === date &&
          e.slot === slot &&
          (slot !== 'CUSTOM' || (e.customSlotName ?? t('mealPlan.customSlot')) === custom),
      )
      .sort((a, b) => a.position - b.position);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const entry = entries.find((e) => e.id === active.id);
    if (!entry) return;
    const target = parseSlotKey(String(over.id));
    if (
      target.date === entry.date &&
      target.slot === entry.slot &&
      (target.customSlotName ?? null) === (entry.slot === 'CUSTOM' ? entry.customSlotName : null)
    )
      return;
    onMove(entry, target);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={onDragEnd}
      accessibility={{
        screenReaderInstructions: { draggable: t('mealPlan.dnd.instructions') },
        announcements: {
          onDragStart: ({ active }) =>
            t('mealPlan.dnd.pickedUp', {
              title: entryTitle(entries.find((e) => e.id === active.id) as MealPlanEntry),
            }),
          onDragOver: ({ over }) =>
            over
              ? t('mealPlan.dnd.over', {
                  slot: describeSlot(parseSlotKey(String(over.id)), t, locale),
                })
              : '',
          onDragEnd: ({ over }) =>
            over
              ? t('mealPlan.dnd.dropped', {
                  slot: describeSlot(parseSlotKey(String(over.id)), t, locale),
                })
              : t('mealPlan.dnd.cancelled'),
          onDragCancel: () => t('mealPlan.dnd.cancelled'),
        },
      }}
    >
      <div className="md:grid md:grid-cols-7 md:gap-2" data-testid="meal-plan-week">
        {days.map((date) => {
          const d = parseISO(date);
          const today = isToday(d);
          return (
            <section
              key={date}
              aria-labelledby={`day-${date}`}
              className={cn('mb-4 rounded-lg border md:mb-0', today && 'border-primary')}
            >
              <h2
                id={`day-${date}`}
                className={cn(
                  'flex items-baseline justify-between gap-2 border-b px-3 py-2 text-sm font-semibold md:flex-col md:items-start md:gap-0',
                  today && 'text-primary',
                )}
              >
                <span>{format(d, 'EEEE', { locale })}</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {format(d, 'P', { locale })}
                </span>
                {today && <span className="sr-only">({t('mealPlan.today')})</span>}
              </h2>
              <div className="space-y-2 p-2">
                {slotRows.map((row) => (
                  <SlotCell
                    key={slotKey(date, row.slot, row.custom)}
                    id={slotKey(date, row.slot, row.custom)}
                    label={row.label}
                    hue={mealSlotHue(row.slot)}
                    entries={entriesFor(date, row.slot, row.custom)}
                    onAdd={() => onAdd({ date, slot: row.slot, customSlotName: row.custom })}
                    onEdit={onEdit}
                    onRemove={onRemove}
                    onMoveRequest={onMoveRequest}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </DndContext>
  );
}

function describeSlot(
  target: SlotTarget,
  t: ReturnType<typeof useTranslation>['t'],
  locale: ReturnType<typeof dateLocale>,
) {
  const label =
    target.slot === 'CUSTOM'
      ? (target.customSlotName ?? t('mealPlan.customSlot'))
      : mealSlotLabel(t, target.slot);
  return `${format(parseISO(target.date), 'EEEE P', { locale })} – ${label}`;
}

function SlotCell({
  id,
  label,
  hue,
  entries,
  onAdd,
  onEdit,
  onRemove,
  onMoveRequest,
}: {
  id: string;
  label: string;
  hue: CategoryHue;
  entries: MealPlanEntry[];
  onAdd: () => void;
  onEdit: (entry: MealPlanEntry) => void;
  onRemove: (entry: MealPlanEntry) => void;
  onMoveRequest: (entry: MealPlanEntry) => void;
}) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded-md border border-dashed p-1.5',
        isOver && 'border-primary bg-accent/50',
      )}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <HueDot hue={hue} /> {label}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label={t('mealPlan.addTo', { slot: label })}
          onClick={onAdd}
        >
          <Plus aria-hidden />
        </Button>
      </div>
      <ul className="mt-1 space-y-1">
        {entries.map((entry) => (
          <EntryCard
            key={entry.id}
            entry={entry}
            onEdit={() => onEdit(entry)}
            onRemove={() => onRemove(entry)}
            onMoveRequest={() => onMoveRequest(entry)}
          />
        ))}
      </ul>
    </div>
  );
}

function EntryCard({
  entry,
  onEdit,
  onRemove,
  onMoveRequest,
}: {
  entry: MealPlanEntry;
  onEdit: () => void;
  onRemove: () => void;
  onMoveRequest: () => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, isDragging } =
    useDraggable({ id: entry.id });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;
  const title = entryTitle(entry);
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-1 rounded-md border bg-card p-1 text-sm shadow-sm',
        isDragging && 'z-20 opacity-80',
      )}
      data-testid="meal-plan-entry"
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        className="touch-none cursor-grab rounded p-1 text-muted-foreground hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
        aria-label={t('mealPlan.dnd.handle', { title })}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" aria-hidden />
      </button>
      {entry.recipeThumbnailUrl && (
        <img
          src={entry.recipeThumbnailUrl}
          alt=""
          className="size-8 shrink-0 rounded object-cover"
        />
      )}
      <span className="min-w-0 flex-1">
        {entry.recipeId ? (
          <Link
            to={`/recipes/${entry.recipeId}`}
            className="block truncate font-medium hover:underline"
          >
            {title}
          </Link>
        ) : (
          <span className="block truncate font-medium">{title}</span>
        )}
        <span className="block truncate text-xs text-muted-foreground">
          {[entry.servings ? `${entry.servings} ${t('common.servings')}` : null, entry.note]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label={t('mealPlan.entryActions', { title })}
          >
            <MoreHorizontal aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={onEdit}>
            <Pencil aria-hidden /> {t('common.edit')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onMoveRequest}>
            <MoveRight aria-hidden /> {t('mealPlan.move')}
          </DropdownMenuItem>
          <DropdownMenuItem destructive onSelect={onRemove}>
            <Trash2 aria-hidden /> {t('common.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}
